import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Campaign, CampaignPackage } from "./useActiveCampaign";

export interface CampaignFormData {
  name: string;
  slug: string;
  description?: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_hidden: boolean;
  hero_title?: string;
  hero_subtitle?: string;
}

export interface CampaignPackageFormData {
  campaign_id: string;
  name: string;
  description?: string;
  price: number;
  original_value?: number;
  discount_percent?: number;
  credits_amount: number;
  includes_nimi_plus: boolean;
  first_time_only: boolean;
  display_order: number;
  is_active: boolean;
  grants_unlimited_days?: number | null;
  unlimited_start_date?: string | null;
  unlimited_end_date?: string | null;
  package_type: string;
  subscription_price_monthly?: number | null;
  subscription_credits_monthly?: number | null;
  trial_days?: number | null;
  partner_code_filter?: string | null;
  tier_slug?: string | null;
  order_limit?: number | null;
}

export function useAdminCampaigns() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const campaignsQuery = useQuery({
    queryKey: ["admin-campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Campaign[];
    },
  });

  const disableOtherNonHiddenCampaigns = async (excludeId?: string) => {
    let query = supabase
      .from("campaigns")
      .update({ is_active: false })
      .eq("is_active", true)
      .eq("is_hidden", false);
    
    if (excludeId) {
      query = query.neq("id", excludeId);
    }
    
    const { error } = await query;
    if (error) throw error;
  };

  const createCampaign = useMutation({
    mutationFn: async (data: CampaignFormData) => {
      // If enabling a non-hidden campaign, disable other non-hidden campaigns
      if (data.is_active && !data.is_hidden) {
        await disableOtherNonHiddenCampaigns();
      }

      const { data: result, error } = await supabase
        .from("campaigns")
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["active-campaign"] });
      toast({ title: "Campaign created successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateCampaign = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CampaignFormData> }) => {
      // If enabling a non-hidden campaign, disable other non-hidden campaigns
      if (data.is_active && !data.is_hidden) {
        await disableOtherNonHiddenCampaigns(id);
      }

      const { error } = await supabase
        .from("campaigns")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["active-campaign"] });
      toast({ title: "Campaign updated successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("campaigns").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["active-campaign"] });
      toast({ title: "Campaign deleted successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting campaign",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    campaigns: campaignsQuery.data || [],
    isLoading: campaignsQuery.isLoading,
    createCampaign,
    updateCampaign,
    deleteCampaign,
  };
}

export function useAdminCampaignPackages(campaignId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const packagesQuery = useQuery({
    queryKey: ["admin-campaign-packages", campaignId],
    queryFn: async () => {
      let query = supabase
        .from("campaign_packages")
        .select("*")
        .order("display_order", { ascending: true });

      if (campaignId) {
        query = query.eq("campaign_id", campaignId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as CampaignPackage[];
    },
    enabled: !!campaignId,
  });

  const createPackage = useMutation({
    mutationFn: async (data: CampaignPackageFormData) => {
      const { data: result, error } = await supabase
        .from("campaign_packages")
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaign-packages"] });
      toast({ title: "Package created successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePackage = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CampaignPackageFormData> }) => {
      const { error } = await supabase
        .from("campaign_packages")
        .update(data)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaign-packages"] });
      toast({ title: "Package updated successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deletePackage = useMutation({
    mutationFn: async (id: string) => {
      // Check if there are any purchases for this package
      const { count, error: countError } = await supabase
        .from("campaign_purchases")
        .select("*", { count: "exact", head: true })
        .eq("package_id", id);

      if (countError) throw countError;

      if (count && count > 0) {
        throw new Error(
          `This package has ${count} purchase(s) and cannot be deleted. Please deactivate it instead to preserve purchase history.`
        );
      }

      const { error } = await supabase.from("campaign_packages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-campaign-packages"] });
      toast({ title: "Package deleted successfully" });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting package",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    packages: packagesQuery.data || [],
    isLoading: packagesQuery.isLoading,
    createPackage,
    updatePackage,
    deletePackage,
  };
}

export function useAdminCampaignPurchases() {
  return useQuery({
    queryKey: ["admin-campaign-purchases"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_purchases")
        .select(`
          *,
          campaign_packages (
            name,
            credits_amount,
            campaigns (name)
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}
